import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Tooltip,
  LinearProgress,
  Alert,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import AssignmentIcon from '@mui/icons-material/Assignment'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { transactionService } from '@/services/transactionService'

interface QueueItem {
  id: string
  type: 'deposit' | 'withdrawal'
  msisdn: string
  amount: number
  status: 'PENDING' | 'PROCESSING'
  priority: 'high' | 'medium' | 'low'
  createdAt: string
  assignedTo?: string
  slaDeadline?: string
  channel?: string
  paybill?: string
}

const TransactionQueue = () => {
  const [queueItems, setQueueItems] = useState<QueueItem[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedItem, setSelectedItem] = useState<QueueItem | null>(null)
  const [actionDialog, setActionDialog] = useState<'approve' | 'reject' | 'assign' | null>(null)
  const [assignTo, setAssignTo] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [filter, setFilter] = useState<'all' | 'pending' | 'processing' | 'high-priority'>('all')
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all')

  useEffect(() => {
    loadQueue()
    const interval = setInterval(loadQueue, 30000) // Refresh every 30 seconds
    return () => clearInterval(interval)
  }, [])

  const loadQueue = async () => {
    setLoading(true)
    try {
      // Fetch pending transactions
      const [deposits, withdrawals] = await Promise.all([
        transactionService.fetchDeposits({ page: 1, page_size: 100, status: 'PENDING' }).catch(() => ({ items: [], pagination: { total: 0 } })),
        transactionService.fetchWithdrawals({ page: 1, page_size: 100, status: 'PENDING' }).catch(() => ({ items: [], pagination: { total: 0 } })),
      ])

      const items: QueueItem[] = [
        ...deposits.items.map((d: any) => ({
          id: d.id,
          type: 'deposit' as const,
          msisdn: d.msisdn,
          amount: d.amount,
          status: (d.status || 'PENDING') as 'PENDING' | 'PROCESSING',
          priority: (d.amount > 50000 ? 'high' : d.amount > 10000 ? 'medium' : 'low') as 'high' | 'medium' | 'low',
          createdAt: d.createdAt,
          channel: d.channel,
          paybill: d.paybill,
          slaDeadline: d.slaDeadline,
        })),
        ...withdrawals.items.map((w: any) => ({
          id: w.id,
          type: 'withdrawal' as const,
          msisdn: w.msisdn,
          amount: w.amount,
          status: (w.status || 'PENDING') as 'PENDING' | 'PROCESSING',
          priority: (w.amount > 50000 ? 'high' : w.amount > 10000 ? 'medium' : 'low') as 'high' | 'medium' | 'low',
          createdAt: w.createdAt,
          channel: w.channel,
          paybill: w.paybill,
          slaDeadline: w.slaDeadline,
        })),
      ].sort((a, b) => {
        // Sort by priority first, then by creation date
        const priorityOrder = { high: 3, medium: 2, low: 1 }
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority]
        }
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      })

      setQueueItems(items)
    } catch (error) {
      console.error('Failed to load queue', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredItems = useMemo(() => {
    return queueItems.filter((item) => {
      if (filter === 'pending' && item.status !== 'PENDING') return false
      if (filter === 'processing' && item.status !== 'PROCESSING') return false
      if (filter === 'high-priority' && item.priority !== 'high') return false
      if (priorityFilter !== 'all' && item.priority !== priorityFilter) return false
      return true
    })
  }, [queueItems, filter, priorityFilter])

  const handleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set())
    } else {
      setSelectedItems(new Set(filteredItems.map((item) => item.id)))
    }
  }

  const handleSelectItem = (id: string) => {
    const newSelected = new Set(selectedItems)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedItems(newSelected)
  }

  const handleBulkApprove = async () => {
    if (selectedItems.size === 0) return
    // TODO: Implement bulk approve API call
    console.log('Bulk approve:', Array.from(selectedItems))
    setSelectedItems(new Set())
    loadQueue()
  }

  const handleBulkReject = async () => {
    if (selectedItems.size === 0) return
    setActionDialog('reject')
  }

  const handleApprove = async (item: QueueItem) => {
    // TODO: Implement approve API call
    console.log('Approve:', item.id)
    loadQueue()
  }

  const handleReject = async (item: QueueItem, reason?: string) => {
    // TODO: Implement reject API call
    console.log('Reject:', item.id, reason)
    setActionDialog(null)
    setRejectionReason('')
    loadQueue()
  }

  const handleAssign = async (item: QueueItem, assignee: string) => {
    // TODO: Implement assign API call
    console.log('Assign:', item.id, assignee)
    setActionDialog(null)
    setAssignTo('')
    loadQueue()
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  const getPriorityColor = (priority: string): 'error' | 'warning' | 'default' => {
    switch (priority) {
      case 'high':
        return 'error'
      case 'medium':
        return 'warning'
      default:
        return 'default'
    }
  }

  const getSlaStatus = (deadline?: string) => {
    if (!deadline) return null
    const now = new Date()
    const deadlineDate = new Date(deadline)
    const hoursRemaining = (deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60)
    if (hoursRemaining < 0) return { status: 'overdue', hours: Math.abs(hoursRemaining) }
    if (hoursRemaining < 2) return { status: 'urgent', hours: hoursRemaining }
    return { status: 'ok', hours: hoursRemaining }
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            Transaction Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage pending transactions requiring manual review
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={handleBulkApprove}
            disabled={selectedItems.size === 0}
          >
            Approve Selected ({selectedItems.size})
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={handleBulkReject}
            disabled={selectedItems.size === 0}
          >
            Reject Selected
          </Button>
        </Stack>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filter}
                label="Status"
                onChange={(e) => setFilter(e.target.value as any)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="high-priority">High Priority</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={priorityFilter}
                label="Priority"
                onChange={(e) => setPriorityFilter(e.target.value as any)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Total: {filteredItems.length} items
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {filteredItems.length === 0 && !loading && (
        <Alert severity="info">No transactions in queue</Alert>
      )}

      {filteredItems.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedItems.size > 0 && selectedItems.size < filteredItems.length}
                    checked={filteredItems.length > 0 && selectedItems.size === filteredItems.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Type</TableCell>
                <TableCell>MSISDN</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>SLA</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item) => {
                const sla = getSlaStatus(item.slaDeadline)
                return (
                  <TableRow
                    key={item.id}
                    hover
                    selected={selectedItems.has(item.id)}
                    sx={{
                      bgcolor: sla?.status === 'overdue' ? 'error.light' : sla?.status === 'urgent' ? 'warning.light' : 'transparent',
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.has(item.id)}
                        onChange={() => handleSelectItem(item.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                        size="small"
                        color={item.type === 'deposit' ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{item.msisdn}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(item.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.priority.toUpperCase()}
                        size="small"
                        color={getPriorityColor(item.priority)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        size="small"
                        color={item.status === 'PROCESSING' ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{item.channel || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatTime(item.createdAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      {sla ? (
                        <Tooltip title={`${sla.hours.toFixed(1)} hours ${sla.status === 'overdue' ? 'overdue' : 'remaining'}`}>
                          <Chip
                            icon={<AccessTimeIcon />}
                            label={sla.hours.toFixed(1) + 'h'}
                            size="small"
                            color={sla.status === 'overdue' ? 'error' : sla.status === 'urgent' ? 'warning' : 'default'}
                          />
                        </Tooltip>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {item.assignedTo ? (
                        <Chip
                          icon={<AssignmentIcon />}
                          label={item.assignedTo}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Unassigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget)
                          setSelectedItem(item)
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            if (selectedItem) handleApprove(selectedItem)
            setAnchorEl(null)
          }}
        >
          <CheckCircleIcon sx={{ mr: 1 }} /> Approve
        </MenuItem>
        <MenuItem
          onClick={() => {
            setActionDialog('reject')
            setAnchorEl(null)
          }}
        >
          <CancelIcon sx={{ mr: 1 }} /> Reject
        </MenuItem>
        <MenuItem
          onClick={() => {
            setActionDialog('assign')
            setAnchorEl(null)
          }}
        >
          <AssignmentIcon sx={{ mr: 1 }} /> Assign
        </MenuItem>
      </Menu>

      {/* Reject Dialog */}
      <Dialog open={actionDialog === 'reject'} onClose={() => setActionDialog(null)}>
        <DialogTitle>Reject Transaction{selectedItems.size > 1 ? 's' : ''}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 2, minWidth: 400 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (selectedItem) {
                handleReject(selectedItem, rejectionReason)
              } else if (selectedItems.size > 0) {
                // Bulk reject
                console.log('Bulk reject:', Array.from(selectedItems), rejectionReason)
                setSelectedItems(new Set())
                setActionDialog(null)
                setRejectionReason('')
                loadQueue()
              }
            }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={actionDialog === 'assign'} onClose={() => setActionDialog(null)}>
        <DialogTitle>Assign Transaction</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Assign To"
            value={assignTo}
            onChange={(e) => setAssignTo(e.target.value)}
            sx={{ mt: 2, minWidth: 400 }}
            placeholder="Enter operator name or email"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (selectedItem && assignTo) {
                handleAssign(selectedItem, assignTo)
              }
            }}
            disabled={!assignTo}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TransactionQueue

